// A wrapper for Bugsnag notify
// - Checks if Bugsnag is enabled for logging
// - Initializes Bugsnag
// - Populates user from session if found
// - Filters out expected network errors that are not actionable
import Bugsnag from '@bugsnag/js';

import { getStoredUser } from '~/domains/analytics';

interface Props {
  errorMessage?: string;
  context?: string;
  severity?: 'info' | 'warning' | 'error';
  details?: object;
  errorDetails?: object;
}

/**
 * Network-related error messages that should not be reported to Bugsnag.
 * These are expected errors that occur due to user connectivity issues,
 * navigation away from the page, or request timeouts.
 * This mirrors the filtering in initBugSnag.ts onError callback.
 */
const IGNORED_ERROR_MESSAGES = [
  'Request aborted',
  'Network Error',
  'Timeout error',
  'AxiosError: Request aborted',
  'AxiosError: Network Error',
  'AxiosError: Timeout error',
  'Paysafe not loaded yet',
];

const shouldIgnoreError = (errorMessage?: string): boolean => {
  if (!errorMessage) return false;
  return IGNORED_ERROR_MESSAGES.some((ignored) => errorMessage.includes(ignored));
};

const bugSnagLog = ({
  errorMessage,
  context = 'unknown',
  severity = 'info',
  details,
  errorDetails,
}: Props) => {
  if (shouldIgnoreError(errorMessage)) {
    return;
  }

  const user = getStoredUser();

  if (process.env.NEXT_PUBLIC_BUGSNAG_ENABLED === 'true') {
    Bugsnag.notify(new Error(`${errorMessage}`), (event) => {
      event.severity = severity;
      event.context = context;
      event.setUser(user?.id, user?.email, user?.handle);
      if (details) {
        event.addMetadata('details', details);
      }
      if (errorDetails) {
        event.addMetadata('error', errorDetails);
      }
    });
  }
};

export { bugSnagLog };
